import { useCallback, useEffect, useState } from "react";
import { ImportService, ItemVerify } from "../../../services/ImportService";
import { ImportLogListResponse } from "../../shared/entities/ImportLogListResponse";
import { TranslationService } from "../../../services/TranslationService";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import { ModalService } from "../../shared/bootstrap/modal/Modal";
import { ToastService } from "../../shared/bootstrap/Toast";
import FloatingPanelService from "../../shared/FloatingPanel";
import Loading from "../../shared/components/Loading";

const ImportLogPausedDetail = ({
  data
}: {
  data: ImportLogListResponse.Item;
}) => {
  const [predecessorsData, setPredecessorsData] = useState<any[] | undefined>(
    undefined
  );
  const [predecessorsIdsValids, setPredecessorsIdsValids] = useState<
    Array<number>
  >([]);
  const [predecessorsVerify, setPredecessorsVerify] =
    useState<ItemVerify[]>(undefined);
  const [isSaving] = useState(false);
  const { translate } = TranslationService;
  const DueActionsRemove = 0;

  useEffect(() => {
    const fetchLog = async () => {
      try {
        const result = await ImportService.verifypredecessors(
          data.ImportID.toString()
        );
        if (result instanceof Error) {
          setPredecessorsData(undefined);
        } else {
          setPredecessorsData(result.predecessors);
          setPredecessorsIdsValids(result.predecessorsIds);
          setPredecessorsVerify(result.predecessorsVerifyList);
        }
      } catch (err) {
        setPredecessorsData(undefined);
      }
    };
    fetchLog();
  }, [data.ImportID]);

  const handleFinishImport = useCallback(() => {
    ModalService.showDefaultModal({
      title: translate.ForceExecutionNow,
      message: translate.ForceExecutionConfirmation,
      acceptButtonLabel: translate.ForceExecutionNow,
      onAcceptClick: async () => {
        const result = await ImportService.forceSuspendProcess(
          data.ImportID.toString()
        );
        if (!(result instanceof Error)) {
          ToastService.showToast(
            translate.ImportProcessStarted,
            undefined,
            "success"
          );
          // setSaving(true)
          FloatingPanelService.hidePanel();
        }
      }
    });
  }, [data.ImportID, translate]);

  const closeModal = useCallback(() => {
    FloatingPanelService.hidePanel();
  }, []);

  const isDue = (predecessor: any): boolean => {
    if (!data.Created) return false;

    const importDate = new Date(data.Created);
    const now = new Date();

    if (predecessor.PostpneIntraDay ?? false) {
      // Comparación de solo fechas (sin hora)
      const importDateOnly = new Date(
        importDate.getFullYear(),
        importDate.getMonth(),
        importDate.getDate()
      );
      const todayOnly = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );

      return importDateOnly.getTime() < todayOnly.getTime();
    } else if (
      predecessor.PostponeHours !== null &&
      predecessor.PostponeHours !== undefined
    ) {
      // Diferencia en horas
      const diffInMilliseconds = now.getTime() - importDate.getTime();
      const diffInHours = diffInMilliseconds / (1000 * 60 * 60);

      return diffInHours > predecessor.PostponeHours;
    }

    return false;
  };

  return (
    <>
      <div className="floatingBody p-4">
        {predecessorsVerify && predecessorsVerify?.length > 0 ? (
          <table
            className={"table genericTable table-ellipsis mb-0"}
            cellPadding="8"
            cellSpacing="0"
          >
            <thead>
              <tr>
                <th>{translate.LogSuspendValidated}</th>
                <th>{translate.LogSuspendProcess}</th>
                <th>{translate.LogSuspendRule}</th>
                <th>{translate.LogSuspendExpired}</th>
                <th>{translate.LogSuspendPending}</th>
                <th>{translate.LogSuspendAction}</th>
              </tr>
            </thead>
            <tbody>
              {predecessorsVerify.map((verifyItem) => {
                // 1. Buscar el predecesor correspondiente en la data
                const predecessor = predecessorsData?.find(
                  (p) =>
                    p.ImportTemplatePredecessorID === verifyItem.predecessorId
                );

                // 2. Lógica: if (predecessors.ContainsKey(tup.Item1))
                const isValid = predecessorsIdsValids.includes(
                  verifyItem.predecessorId
                );

                if (!predecessor && !isValid) return null;

                // 3. Lógica de la regla (IntraDay vs Hours)
                let ruleText = "";
                if (predecessor.IntraDay ?? false) {
                  ruleText = translate.IntraDayImport;
                } else if (predecessor.Hours) {
                  ruleText =
                    translate.RecentHoursImport +
                    predecessor.Hours +
                    translate.Hours;
                }

                const expired = isDue(predecessor);

                return (
                  <tr key={verifyItem.predecessorId}>
                    {/* Validado: Rojo si no es válido */}
                    <td
                      style={{ color: verifyItem.expired ? "inherit" : "red" }}
                    >
                      {verifyItem.expired ? translate.Yes : translate.No}
                    </td>

                    {/* Nombre del Proceso */}
                    <td>{predecessor.ImportTemplatePredecessing.Name}</td>

                    {/* Regla */}
                    <td>{ruleText}</td>

                    {/* Expirado: Rojo si verifyItem.expired es true */}
                    <td style={{ color: expired ? "red" : "inherit" }}>
                      {expired ? translate.Yes : translate.No}
                    </td>

                    {/* Pendiente / Postergación */}
                    <td>
                      {(predecessor.PostpneIntraDay ?? true)
                        ? translate.IntraDay
                        : `${predecessor.PostponeHours ?? 0} ${translate.Hours?.toLowerCase()}`}
                    </td>

                    {/* Acción (Discard vs Process) */}
                    <td>
                      {predecessor.postponeDueAction === DueActionsRemove
                        ? translate.Discard
                        : translate.Process}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Loading />
        )}
      </div>

      <FloatingPanelFooter>
        <button className="btn btn-secondary" onClick={handleFinishImport}>
          {translate.ForceExecutionNow}
        </button>
        <button className="btn btn-primary me-3" onClick={closeModal}>
          {translate.Save}
          {isSaving && (
            <i className="fas fa-spinner-third fa-spin third ms-2"></i>
          )}
        </button>
      </FloatingPanelFooter>
    </>
  );
};

export default ImportLogPausedDetail;
